import { createContext, useContext, ReactNode } from 'react'
import { useTeam } from '@/hooks/useTeam'
import type { TeamMember, CurrentTeamResponse, ListTeamsResponse, TeamInviteDetails, TeamMemberRole } from '@shared/types'

interface TeamContextType {
  // Current team data
  currentTeam: CurrentTeamResponse | undefined
  isLoadingCurrentTeam: boolean
  currentTeamError: Error | null
  refetchCurrentTeam: () => void

  // All teams data
  allTeams: ListTeamsResponse | undefined
  isLoadingAllTeams: boolean
  allTeamsError: Error | null
  refetchAllTeams: () => void

  // Team members data
  teamMembers: { members: TeamMember[]; teamId: string; ownerId: string } | undefined
  isLoadingMembers: boolean
  membersError: Error | null
  refetchMembers: () => void

  // Mutations
  switchTeam: (teamId: string) => Promise<any>
  isSwitchingTeam: boolean
  switchTeamError: Error | null

  createInvite: (role?: 'admin' | 'member') => Promise<any>
  isCreatingInvite: boolean
  createInviteError: Error | null

  removeMember: (userId: string) => Promise<any>
  isRemovingMember: boolean
  removeMemberError: Error | null

  updateMemberRole: (params: { userId: string; role: 'admin' | 'member' }) => Promise<any>
  isUpdatingMemberRole: boolean
  updateMemberRoleError: Error | null

  leaveTeam: () => Promise<any>
  isLeavingTeam: boolean
  leaveTeamError: Error | null

  acceptInvite: (token: string) => Promise<any>
  isAcceptingInvite: boolean
  acceptInviteError: Error | null

  createTeam: (organizationName?: string) => Promise<any>
  isCreatingTeam: boolean
  createTeamError: Error | null

  // Team invites data
  teamInvites: TeamInviteDetails[] | undefined
  isLoadingInvites: boolean
  invitesError: Error | null
  refetchInvites: () => void

  deleteInvite: (inviteId: string) => Promise<any>
  isDeletingInvite: boolean
  deleteInviteError: Error | null

  // Computed properties
  isTeamOwner: boolean
  hasMultipleTeams: boolean
  ownsTeam: boolean
  isLoading: boolean

  // Role-based properties
  currentUserRole: TeamMemberRole | null
  canInvite: boolean
  canRemoveMember: boolean
  canManageBilling: boolean
  canUpdateRoles: boolean
}

const TeamContext = createContext<TeamContextType | undefined>(undefined)

export function TeamProvider({ children }: { children: ReactNode }) {
  const team = useTeam()

  return (
    <TeamContext.Provider value={team}>
      {children}
    </TeamContext.Provider>
  )
}

export function useTeamContext() {
  const context = useContext(TeamContext)
  if (context === undefined) {
    throw new Error('useTeamContext must be used within a TeamProvider')
  }
  return context
}

export function useOptionalTeamContext() {
  return useContext(TeamContext)
}
